// Pixar Whiteboard Ad's draft as the form keeps it (spec G14-pixar-whiteboard-ad, PR g1): the server
// holds the member's draft (`.../templates/pixar-whiteboard-ad/draft`), so it survives the panel;
// the form reads it, keeps its edits there, and asks the server's pack reader, directors and stills
// (live's `usePixarWhiteboardDraft`, which ran them from the browser). As Chalk Explainer Ad's
// (`chalk-draft.ts`), with live's "Write the 10 lines yourself", no "Back to script" and the takes;
// the timing plan and what keeps the script from approval come back on each write.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetPixarWhiteboardAdDraftQueryKey,
  useApprovePixarWhiteboardAdScript,
  useApprovePixarWhiteboardAdStoryboard,
  useGetPixarWhiteboardAdDraft,
  useRegeneratePixarWhiteboardAdStill,
  useResetPixarWhiteboardAdDraft,
  useWritePixarWhiteboardAdDraft,
  useWritePixarWhiteboardAdPick,
  useWritePixarWhiteboardAdScript,
  useWritePixarWhiteboardAdTake,
  writePixarWhiteboardAdDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  PixarWhiteboardAdDraft,
  PixarWhiteboardDraftStill,
  PixarWhiteboardStillName,
  PixarWhiteboardTake,
  PixarWhiteboardTakeClip,
  WhiteboardScript,
} from "../../../api/generated/model";
import {
  PixarWhiteboardAdApproval,
  PixarWhiteboardAdDraftWrite,
  PixarWhiteboardAdInputs,
  PixarWhiteboardAdScriptAsk,
  PixarWhiteboardAdShoot,
  type PixarWhiteboardAdDraftWriteOutput,
  type PixarWhiteboardAdInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";
import { blankScript, sameLineTexts } from "./pixar-whiteboard-script";

/** The form as it stands: its picks and its script. */
export interface WhiteboardForm {
  inputs: PixarWhiteboardAdInputsOutput;
  script: WhiteboardScript | null;
}

/** Live's eight stills in display order (`STILL_GRAPH.order`). */
export const STILL_NAMES: readonly PixarWhiteboardStillName[] = [
  "sheet",
  "master_board",
  "char_ref",
  "opening",
  "anchor_a",
  "anchor_b",
  "anchor_c",
  "end_card",
];

/** What each still is drawn from (live's `STILL_GRAPH.sources`); the sheet, the master board and
 * the end card from no still. */
const STILL_SOURCES: Readonly<Partial<Record<PixarWhiteboardStillName, readonly PixarWhiteboardStillName[]>>> = {
  char_ref: ["sheet", "master_board"],
  opening: ["char_ref"],
  anchor_a: ["opening", "master_board"],
  anchor_b: ["anchor_a", "master_board"],
  anchor_c: ["anchor_b", "master_board"],
};

/** A still under way (live's `isStillWaiting`): drawing, or pending on stills that are all done or
 * still coming. */
export function stillWaiting(name: PixarWhiteboardStillName, stills: readonly PixarWhiteboardDraftStill[]): boolean {
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  const sources = STILL_SOURCES[name] ?? [];
  return (
    sources.length > 0 &&
    sources.every((source) => stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, stills))
  );
}

/** The draft refetched while it works: every 2 s while a director or a still works, and a moment
 * after (a step's end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** Where the draft's work stands, as live's progress line names it (`run-progress.ts`). */
export interface DraftWork {
  reading: boolean;
  writing: boolean;
  boarding: boolean;
  drawing: boolean;
  /** A take is shot or heard. */
  shooting: boolean;
}

export function workOf(draft: PixarWhiteboardAdDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  return {
    reading: draft.pack_director === "working",
    writing: draft.script_director === "working" || (answering && draft.pack_director !== "working"),
    boarding: draft.board_director === "working",
    drawing: draft.stills.some((each) => stillWaiting(each.name, draft.stills)),
    shooting: draft.takes.some((clip) => clip.takes.some((take) => take.status === "working")),
  };
}

function working(work: DraftWork): boolean {
  return work.reading || work.writing || work.boarding || work.drawing || work.shooting;
}

/** The clip's picked take, if it has one. */
function pickedTake(draft: PixarWhiteboardAdDraft, clip: PixarWhiteboardTakeClip): PixarWhiteboardTake | null {
  const row = draft.takes.find((each) => each.clip === clip);
  return row?.takes.find((take) => take.number === row.picked) ?? null;
}

/** Every clip has its take picked, the end card included (live's `hasEveryTakePicked`). */
export function everyTakePicked(draft: PixarWhiteboardAdDraft): boolean {
  return draft.takes.length > 0 && draft.takes.every((row) => row.picked !== null);
}

/** The captions, which stay open until the final video (live's footer). */
export type CaptionSettings = Pick<PixarWhiteboardAdInputsOutput, "subtitles" | "caption_position" | "caption_style_id">;

/** "Generate final video"'s body (live's hand-off): the approved script, each clip's picked take
 * with its length and heard words, the end card's, and the captions; null until all are there. */
export function videoBody(draft: PixarWhiteboardAdDraft, captions: CaptionSettings) {
  if (draft.script === null) return null;
  const talking = (clip: "A" | "B" | "C") => {
    const take = pickedTake(draft, clip);
    if (!take?.output || take.duration_s === null) return null;
    return { video: take.output, seconds: take.duration_s, heard: take.heard };
  };
  const [a, b, c] = [talking("A"), talking("B"), talking("C")];
  const endCard = pickedTake(draft, "end_card")?.output;
  if (a === null || b === null || c === null || !endCard) return null;
  return {
    type: "video.template" as const,
    inputs: {
      template: "pixar-whiteboard-ad" as const,
      script: draft.script,
      takes: { a, b, c, end_card: endCard },
      ...captions,
    },
  };
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: PixarWhiteboardAdDraft): WhiteboardForm {
  const inputs = PixarWhiteboardAdInputs.safeParse(draft.inputs);
  return {
    inputs: inputs.success ? inputs.data : PixarWhiteboardAdInputs.parse({}),
    script: draft.script,
  };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses, whose answer
 * brings the timing plan and what keeps the script from approval; each ask answers with the draft,
 * shown from then on.
 */
export function usePixarWhiteboardDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetPixarWhiteboardAdDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<WhiteboardForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  // The lines were edited since the script came: only then does the gate's issue show (live's
  // `isScriptEdited`).
  const [edited, setEdited] = useState(false);
  const lastBusy = useRef(0);
  const read = useGetPixarWhiteboardAdDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll: number | false = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: PixarWhiteboardAdDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWritePixarWhiteboardAdScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApprovePixarWhiteboardAdScript(asks);
  const regeneration = useRegeneratePixarWhiteboardAdStill(asks);
  const shooting = useApprovePixarWhiteboardAdStoryboard(asks);
  const takeAsk = useWritePixarWhiteboardAdTake(asks);
  const pickAsk = useWritePixarWhiteboardAdPick(asks);
  const reset = useResetPixarWhiteboardAdDraft(asks);
  const asking =
    script.isPending ||
    approval.isPending ||
    regeneration.isPending ||
    shooting.isPending ||
    takeAsk.isPending ||
    pickAsk.isPending ||
    reset.isPending;
  const work: DraftWork =
    draft === undefined
      ? { reading: false, writing: false, boarding: false, drawing: false, shooting: false }
      : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWritePixarWhiteboardAdDraft({
    mutation: {
      onSuccess: (answer) => {
        // The edits stay the form's own; the answer brings what the server reads from them.
        queryClient.setQueryData(key, answer);
        setFailure(null);
      },
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: PixarWhiteboardAdDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writePixarWhiteboardAdDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = PixarWhiteboardAdDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    form,
    work,
    busy,
    editable,
    edited,
    failure,
    approving: approval.isPending,
    shooting: shooting.isPending,
    resetting: reset.isPending,
    editScript(next: WhiteboardScript) {
      if (server === null) return;
      const before = local?.script ?? server.script;
      if (before !== null && !sameLineTexts(before, next)) setEdited(true);
      setLocal((current) => ({ ...(current ?? server), script: next }));
    },
    editInputs(change: Partial<PixarWhiteboardAdInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = PixarWhiteboardAdScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      setEdited(false);
      script.mutate({ teamId, brandId, data: body.data });
    },
    /** "Write the 10 lines yourself": live's blank script, kept with the form. */
    writeScript() {
      if (server === null) return;
      ask();
      setEdited(false);
      setLocal((current) => ({ ...(current ?? server), script: blankScript() }));
    },
    approve() {
      if (form === null) return;
      const body = PixarWhiteboardAdApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    regenerate(name: PixarWhiteboardStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    /** "Shoot the clips": every take of every clip, at the resolution and takes Approve kept. */
    shoot() {
      if (form === null) return;
      const body = PixarWhiteboardAdShoot.safeParse({
        resolution: form.inputs.resolution,
        takes_per_clip: form.inputs.takes_per_clip,
      });
      if (!body.success) return;
      ask();
      shooting.mutate({ teamId, brandId, data: body.data });
    },
    /** "New take": one more take of a clip, picked only where it had none. */
    newTake(clip: PixarWhiteboardTakeClip) {
      ask();
      takeAsk.mutate({ teamId, brandId, clip });
    },
    pick(clip: PixarWhiteboardTakeClip, take: number) {
      ask();
      pickAsk.mutate({ teamId, brandId, clip, data: { take } });
    },
    startOver() {
      ask();
      setAsked(null);
      setEdited(false);
      reset.mutate({ teamId, brandId });
    },
  };
}
